import React, { useState, useEffect } from "react";
import {
  Building,
  Rocket,
  Copy,
  Check,
  AlertTriangle,
  RefreshCw,
  Palette,
  Bot,
  Layout,
} from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";

const AUTH_URL = process.env.REACT_APP_AUTH_URL || "/auth";

const PINECONE_INDEXES = [
  { value: "gop", label: "GOP (Political)" },
  { value: "ltn", label: "LTN (Liberty Tree)" },
  { value: "email2", label: "Email2 (General)" },
  { value: "trm", label: "TRM (Recruiting)" },
];

const VERTICALS = [
  { value: "political", label: "Political & Public Affairs" },
  { value: "healthcare", label: "Healthcare" },
  { value: "legal", label: "Legal" },
  { value: "finance", label: "Finance" },
  { value: "retail", label: "Retail / E-Commerce" },
  { value: "manufacturing", label: "Manufacturing" },
  { value: "construction", label: "Construction" },
];

const INDEX_VERTICAL_MAP = {
  gop: "political",
  ltn: "political",
  email2: "retail",
  trm: "retail",
};

const FacilityProvisioning = () => {
  const { token } = useAuth();

  // Form state
  const [facilityId, setFacilityId] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [state, setState] = useState("");
  const [agentName, setAgentName] = useState("Lidy");
  const [brandColor, setBrandColor] = useState("#2563eb");
  const [pineconeIndex, setPineconeIndex] = useState("gop");
  const [verticalId, setVerticalId] = useState("political");
  const [templateCode, setTemplateCode] = useState("");

  // UI state
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState(false);

  const headers = {
    Authorization: `Bearer ${token}`,
    "Content-Type": "application/json",
  };

  // Fetch available KB templates
  useEffect(() => {
    const fetchTemplates = async () => {
      try {
        const res = await fetch(`${AUTH_URL}/kb-templates`, { headers });
        if (res.ok) {
          const data = await res.json();
          setTemplates(Array.isArray(data) ? data : []);
        }
      } catch (err) {
        console.error("Failed to load templates:", err);
      }
    };
    fetchTemplates();
  }, []);

  // Auto-generate facility ID from display name
  const handleNameChange = (name) => {
    setDisplayName(name);
    if (!facilityId || facilityId === deriveId(displayName)) {
      setFacilityId(deriveId(name));
    }
  };

  const deriveId = (name) => {
    return name
      .toUpperCase()
      .replace(/[^A-Z0-9]/g, "")
      .slice(0, 12);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setResult(null);

    try {
      const res = await fetch(`${AUTH_URL}/provision-facility`, {
        method: "POST",
        headers,
        body: JSON.stringify({
          facility_id: facilityId.toUpperCase(),
          display_name: displayName,
          state: state.toUpperCase(),
          agent_name: agentName,
          brand_color: brandColor,
          pinecone_index: pineconeIndex,
          vertical_id: verticalId,
          template_code: templateCode,
        }),
      });

      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.error || `HTTP ${res.status}`);
      }
      setResult(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const copySnippet = () => {
    if (result?.embed_snippet) {
      navigator.clipboard.writeText(result.embed_snippet);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const resetForm = () => {
    setFacilityId("");
    setDisplayName("");
    setState("");
    setAgentName("Lidy");
    setBrandColor("#2563eb");
    setPineconeIndex("gop");
    setVerticalId("political");
    setTemplateCode("");
    setResult(null);
    setError(null);
  };

  // Show success state
  if (result) {
    return (
      <div className="space-y-6">
        <div className="bg-green-50 border border-green-200 rounded-xl p-8 text-center">
          <Check className="w-12 h-12 text-green-600 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-green-900 mb-2">
            Facility Provisioned
          </h2>
          <p className="text-green-700 mb-1">
            <strong>{result.display_name}</strong> ({result.facility_id}) is
            ready.
          </p>
          <p className="text-sm text-green-600">
            Namespace: {result.pinecone_namespace}
            {result.template_applied && " | KB template applied"}
          </p>
        </div>

        {/* Embed Snippet */}
        <div className="bg-white border border-slate-200 rounded-xl p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-semibold text-slate-700">
              Widget Embed Snippet
            </h3>
            <button
              onClick={copySnippet}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg border border-slate-300 text-slate-700 hover:bg-slate-50 transition-colors"
            >
              {copied ? (
                <Check className="w-4 h-4 text-green-600" />
              ) : (
                <Copy className="w-4 h-4" />
              )}
              {copied ? "Copied" : "Copy"}
            </button>
          </div>
          <pre className="bg-slate-900 text-green-400 rounded-lg p-4 text-xs overflow-x-auto whitespace-pre-wrap">
            {result.embed_snippet}
          </pre>
          <p className="text-xs text-slate-500 mt-3">
            Add this snippet to any webpage to embed the chatbot widget.
          </p>
        </div>

        {/* Next Steps */}
        <div className="bg-white border border-slate-200 rounded-xl p-6">
          <h3 className="text-sm font-semibold text-slate-700 mb-3">
            Next Steps
          </h3>
          <ol className="space-y-2 text-sm text-slate-600 list-decimal list-inside">
            <li>
              Upload knowledge base documents via{" "}
              <strong>Document Manager</strong>
            </li>
            <li>Approve documents and run the embedding pipeline</li>
            <li>Test the chatbot using the embed snippet above</li>
            <li>Share the embed snippet with the facility admin</li>
          </ol>
        </div>

        <button
          onClick={resetForm}
          className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg border border-slate-300 text-slate-700 hover:bg-slate-50 transition-colors"
        >
          <RefreshCw className="w-4 h-4" />
          Provision Another Facility
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-bold text-slate-800 flex items-center gap-2">
          <Rocket className="w-5 h-5 text-blue-600" />
          Provision New Facility
        </h2>
        <p className="text-sm text-slate-500 mt-1">
          Create a new chatbot facility with knowledge base, Pinecone namespace,
          and widget embed snippet.
        </p>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-red-900">
              Provisioning Failed
            </p>
            <p className="text-sm text-red-700 mt-0.5">{error}</p>
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Basic Info */}
        <div className="bg-white border border-slate-200 rounded-xl p-6">
          <div className="flex items-center gap-2 mb-4">
            <Building className="w-4 h-4 text-slate-500" />
            <h3 className="text-sm font-semibold text-slate-700">
              Facility Details
            </h3>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-slate-500 mb-1">
                Display Name *
              </label>
              <input
                type="text"
                value={displayName}
                onChange={(e) => handleNameChange(e.target.value)}
                placeholder="Arizona Legislative District 4"
                required
                maxLength={100}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-slate-500 mb-1">
                Facility ID *
              </label>
              <input
                type="text"
                value={facilityId}
                onChange={(e) =>
                  setFacilityId(
                    e.target.value.toUpperCase().replace(/[^A-Z0-9_]/g, ""),
                  )
                }
                placeholder="AZLD4"
                required
                maxLength={20}
                pattern="[A-Z0-9_]{2,20}"
                className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm font-mono focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
              />
              <p className="text-xs text-slate-400 mt-1">
                Uppercase letters, numbers, underscores only
              </p>
            </div>

            <div>
              <label className="block text-xs font-medium text-slate-500 mb-1">
                State *
              </label>
              <input
                type="text"
                value={state}
                onChange={(e) =>
                  setState(e.target.value.toUpperCase().replace(/[^A-Z]/g, ""))
                }
                placeholder="AZ"
                required
                maxLength={2}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-slate-500 mb-1">
                Pinecone Index
              </label>
              <select
                value={pineconeIndex}
                onChange={(e) => {
                  setPineconeIndex(e.target.value);
                  setVerticalId(INDEX_VERTICAL_MAP[e.target.value] || "retail");
                }}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
              >
                {PINECONE_INDEXES.map((idx) => (
                  <option key={idx.value} value={idx.value}>
                    {idx.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-xs font-medium text-slate-500 mb-1">
                Vertical
              </label>
              <select
                value={verticalId}
                onChange={(e) => setVerticalId(e.target.value)}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
              >
                {VERTICALS.map((v) => (
                  <option key={v.value} value={v.value}>
                    {v.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Branding */}
        <div className="bg-white border border-slate-200 rounded-xl p-6">
          <div className="flex items-center gap-2 mb-4">
            <Palette className="w-4 h-4 text-slate-500" />
            <h3 className="text-sm font-semibold text-slate-700">Branding</h3>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-slate-500 mb-1">
                <Bot className="w-3 h-3 inline mr-1" />
                Agent Name
              </label>
              <input
                type="text"
                value={agentName}
                onChange={(e) => setAgentName(e.target.value)}
                placeholder="Lidy"
                maxLength={30}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-slate-500 mb-1">
                Brand Color
              </label>
              <div className="flex items-center gap-2">
                <input
                  type="color"
                  value={brandColor}
                  onChange={(e) => setBrandColor(e.target.value)}
                  className="w-10 h-10 rounded border border-slate-300 cursor-pointer"
                />
                <input
                  type="text"
                  value={brandColor}
                  onChange={(e) => setBrandColor(e.target.value)}
                  className="flex-1 px-3 py-2 border border-slate-300 rounded-lg text-sm font-mono focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
                />
              </div>
            </div>
          </div>
        </div>

        {/* KB Template */}
        <div className="bg-white border border-slate-200 rounded-xl p-6">
          <div className="flex items-center gap-2 mb-4">
            <Layout className="w-4 h-4 text-slate-500" />
            <h3 className="text-sm font-semibold text-slate-700">
              Knowledge Base Template
            </h3>
          </div>

          <select
            value={templateCode}
            onChange={(e) => setTemplateCode(e.target.value)}
            className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
          >
            <option value="">No template (blank configuration)</option>
            {templates.map((t) => (
              <option key={t.template_code || t.id} value={t.template_code}>
                {t.template_name || t.template_code}
              </option>
            ))}
          </select>
          <p className="text-xs text-slate-400 mt-1">
            Templates pre-configure the system prompt, enrichment rules, and
            agent persona for common industries.
          </p>
        </div>

        {/* Submit */}
        <div className="flex items-center gap-4">
          <button
            type="submit"
            disabled={loading || !facilityId || !displayName || !state}
            className="inline-flex items-center gap-2 px-6 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm font-medium"
          >
            {loading ? (
              <RefreshCw className="w-4 h-4 animate-spin" />
            ) : (
              <Rocket className="w-4 h-4" />
            )}
            {loading ? "Provisioning..." : "Provision Facility"}
          </button>
        </div>
      </form>
    </div>
  );
};

export default FacilityProvisioning;
